<template>
  <RfDialog
    ref="newTriggerDialog"
    :width="width || 800"
    :title="creating ? 'Create Trigger' : 'Update Trigger'"
    :confirmText="creating ? 'Create' : 'Update'"
    :cancelText="userStore.isDisabledWithMessage ? 'Close' : 'Cancel'"
    :disabled="confirmDisabledMessage"
    :loading="submitting"
    keepOpenOnConfirm
    @confirm="submitTrigger"
    @close="handleDialogClose"
  >
    <div v-if="editingTrigger" class="flex flex-col">
      <div class="settings-header flex items-center justify-between gap-5 pb-2">
        <RfButtonTabs :active="activeTabIndex" class="min-w-1">
          <RfButtonNew
            v-for="(tab, i) in tabs"
            :key="tab.value"
            type="plain"
            size="large"
            class="[&_.rf-button--container]:text-beige-50 px-4 py-2"
            :class="{
              '[&_.rf-button--container]:text-black-2 [&_.rf-button--container]:font-bold!':
                i === activeTabIndex,
            }"
            :disabled="isTabDisabled"
            @click="changeTab(tab.value)"
          >
            {{ tab.label }}
          </RfButtonNew>
        </RfButtonTabs>
        <RfSwitch
          v-show="triggerType === 'web'"
          :modelValue="filterModeDisabled"
          class="mt-0 mb-2 min-w-1"
          title="Classic view"
          :disabled="userStore.isReadOnly"
          @update:modelValue="handleFilterModeToggle"
        />
      </div>
      <div>
        <RfInput
          v-model.trim="editingTrigger.name"
          variant="outlined"
          title="Name"
          requiredTitle
          autoWidth
          class="w-full"
          :disabled="userStore.isReadOnly"
          placeholder="e.g.: All Pages"
        />
        <div class="trigger-settings-div p-0">
          <div v-if="triggerType === 'push_event'" class="mt-2 flex flex-col gap-2">
            <RfSelect
              v-model="editingTrigger.event_name"
              variant="outlined"
              sameWidth
              itemValue="name"
              itemKey="name"
              :disabled="userStore.isReadOnly"
              title="Event name"
              placeholder="Example event name"
              :items="pushEventOptions"
            />
            <RfAdvancedArguments
              v-if="editingTrigger.event_name"
              ref="advancedArgsBody"
              :loadedArgs="editingTrigger.event_attributes"
            />
          </div>
          <template v-if="['web', 'device'].includes(triggerType)">
            <div class="trigger-setting-page mt-5 pr-0">
              <label class="text-body text-grey-1">Show on</label>
              <RfRadioGroup
                v-model="pageScope"
                vertical
                class="mt-1"
                :disabled="userStore.isReadOnly"
                :options="pageScopeOptions"
              />
              <div v-if="!anyPageSelected" class="trigger-specific-pg-opt">
                <div v-if="filterModeDisabled">
                  <RfButtonTabs :active="regexTabIndex">
                    <RfButtonNew
                      v-for="(tab, i) in [
                        { label: 'Wildcards(*)', value: false },
                        { label: 'Regular expressions', value: true },
                      ]"
                      :key="tab.label"
                      type="plain"
                      size="large"
                      class="[&_.rf-button--container]:text-beige-50 px-4 py-2"
                      :class="{
                        '[&_.rf-button--container]:text-black-2 [&_.rf-button--container]:font-bold!':
                          i === regexTabIndex,
                      }"
                      @click="editingTrigger.use_regex = tab.value"
                    >
                      {{ tab.label }}
                    </RfButtonNew>
                  </RfButtonTabs>
                  <RfInput
                    v-model.trim="editingTrigger.url_path"
                    class="mb-2 w-full"
                    variant="outlined"
                    :title="settings.url_path_label"
                    :disabled="userStore.isReadOnly"
                    :placeholder="settings.url_path_placeholder"
                    autoWidth
                    :errorMessage="urlFieldErrorMessage(editingTrigger.url_path, true)"
                  />
                  <template v-if="triggerType !== 'device'">
                    <RfInput
                      v-model.trim="editingTrigger.query_params"
                      class="mb-2 w-full"
                      variant="outlined"
                      :disabled="userStore.isReadOnly"
                      :title="settings.query_params_label"
                      :placeholder="settings.query_params_placeholder"
                      autoWidth
                      :errorMessage="urlFieldErrorMessage(editingTrigger.query_params)"
                    />
                    <RfInput
                      v-model.trim="editingTrigger.url_hash"
                      class="mb-2 w-full"
                      variant="outlined"
                      :disabled="userStore.isReadOnly"
                      :title="settings.url_hash_label"
                      :placeholder="settings.url_hash_placeholder"
                      autoWidth
                      :errorMessage="urlFieldErrorMessage(editingTrigger.url_hash)"
                    />
                    <RfInput
                      v-model.trim="editingTrigger.url_host"
                      class="mb-2 w-full"
                      variant="outlined"
                      :disabled="userStore.isReadOnly"
                      :title="settings.url_host_label"
                      :placeholder="settings.url_host_placeholder"
                      autoWidth
                      :errorMessage="urlFieldErrorMessage(editingTrigger.url_host)"
                    />
                  </template>
                  <RfButtonNew
                    v-if="regexAllowed"
                    :disabled="
                      userStore.isReadOnly ||
                      (!containsRegexOrWildcards && `Add a ${testType.toLowerCase()} to test it`) ||
                      (!isTriggerFormValid && 'Fix the field errors above before testing')
                    "
                    :text="testerLabel"
                    type="link"
                    size="link"
                    @click.stop="$refs.regexTester.open()"
                  />
                </div>
                <div v-if="!filterModeDisabled">
                  <div
                    v-for="(row, index) in filterRows"
                    :key="index"
                    class="flex w-full items-center not-first:mt-2"
                  >
                    <RfSelect
                      id="type"
                      v-model="row.filterType"
                      variant="outlined"
                      :items="filterTypes"
                      itemValue="name"
                      itemKey="value"
                      class="segment-builder--type _type w-52 min-w-52 flex-1 grow-0"
                      :class="{ grow: row.filterType }"
                      autoWidth
                      sameWidth
                      placeholder="Select"
                      :disabled="userStore.isReadOnly"
                      @update:modelValue="onFilterTypeSelected(index)"
                    />

                    <template v-if="row.filterType">
                      <RfChevronIcon class="fill-beige-50 h-6 w-6 shrink-0 rotate-180" />
                      <RfSelect
                        v-model="row.matchType"
                        class="w-28 min-w-28 flex-1 shrink-0 grow-0"
                        variant="outlined"
                        autoWidth
                        :items="matchTypeItems"
                        sameWidth
                        :itemDisabled="item => isMatchTypeDisabled(row, item.value)"
                        itemValue="name"
                        itemKey="value"
                        placeholder="Match Type"
                      />
                    </template>
                    <template v-if="row.filterType">
                      <RfChevronIcon class="fill-beige-50 h-6 w-6 shrink-0 rotate-180" />
                      <RfSelect
                        class="min-w-52 flex-1 shrink-0 grow"
                        variant="outlined"
                        sameWidth
                        multiple
                        autoWidth
                        extended
                        visibleCount="3"
                        :disabled="userStore.isReadOnly"
                        :customValues="row.filterType !== 'url_host'"
                        :items="row.filterType === 'url_host' ? rowValueItems(row) : []"
                        :customValueSeeds="row.filterType === 'url_host' ? [] : rowValueItems(row)"
                        :modelValue="row.values"
                        @update:modelValue="values => updateRowValues(index, values)"
                        @removeCustomValue="value => removeRowValue(index, value)"
                      />
                    </template>
                    <div class="buttons mr-0 ml-auto flex shrink-0 gap-2 pl-2">
                      <RfButtonNew type="table" class="shrink-0 grow-0" @click.prevent="addRow">
                        <RfAddIcon class="h-6 w-6" />
                      </RfButtonNew>

                      <RfButtonNew
                        v-if="filterRows.length > 1"
                        type="table"
                        class="shrink-0 grow-0"
                        @click.prevent="removeRow(index)"
                      >
                        <RfDeleteIcon class="fill-vermillion-50 h-6 w-6" />
                      </RfButtonNew>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!filterModeDisabled">
                <div
                  v-if="
                    isDelayClickTriggerOpen || (localTriggerOption && localTriggerOption !== 'none')
                  "
                  class="trigger-settings my-4 rounded-sm p-2 px-0"
                >
                  <div class="settings-header flex items-center justify-between gap-5">
                    <RfButtonTabs :active="delayClickTabIndex">
                      <RfButtonNew
                        v-for="(tab, i) in [
                          { label: 'Delay', value: 'delay' },
                          { label: 'Click Trigger', value: 'click' },
                        ]"
                        :key="tab.value"
                        type="plain"
                        size="large"
                        class="[&_.rf-button--container]:text-beige-50 px-4 py-2"
                        :class="{
                          '[&_.rf-button--container]:text-black-2 [&_.rf-button--container]:font-bold!':
                            i === delayClickTabIndex,
                        }"
                        @click="localTriggerOption = tab.value"
                      >
                        {{ tab.label }}
                      </RfButtonNew>
                    </RfButtonTabs>
                  </div>
                  <div
                    v-if="
                      localTriggerOption === 'delay' ||
                      (localTriggerOption === 'click' &&
                        triggerType === 'device' &&
                        app?.flags?.device_trigger_click_delay)
                    "
                    class="pt-2"
                  >
                    <RfNumberStepper
                      v-model="editingTrigger.delay_seconds"
                      title="Delay (optional)"
                      unit="s"
                      class="w-48"
                      minMessage="Delay cannot be less than 0"
                      :disabled="userStore.isReadOnly"
                    />
                  </div>
                  <div v-if="localTriggerOption === 'click'" class="flex flex-col gap-2.5 pt-2">
                    <RfInput
                      v-model="editingTrigger.click_id"
                      :title="settings.click_id_label"
                      variant="outlined"
                      placeholder="e.g. #cancel"
                      :disabled="userStore.isReadOnly"
                      @update:modelValue="
                        val => {
                          editingTrigger.click_id = val || null;
                        }
                      "
                    />
                    <RfNumberStepper
                      v-model="editingTrigger.click_count_threshold"
                      title="Show after (optional)"
                      :unit="clicksUnitLabel"
                      class="mt-2 w-48"
                      minMessage="Clicks cannot be less than 0"
                      :disabled="userStore.isReadOnly"
                    />
                  </div>
                </div>
                <div
                  v-if="isAdvancedTriggerOpen || editingTrigger.custom_trigger_id"
                  class="mt-5 flex flex-col gap-4"
                >
                  <p class="text-heading-2 -mb-2">Advanced</p>
                  <RfSelect
                    v-if="
                      triggerType !== 'device' ||
                      (triggerType === 'device' && app?.flags?.custom_devices_js_sdk)
                    "
                    v-model="editingTrigger.custom_trigger_id"
                    variant="outlined"
                    itemKey="value"
                    itemValue="title"
                    class="max-w-1/2"
                    :disabled="userStore.isReadOnly"
                    :items="customTriggers"
                  >
                    <template #title>
                      <span class="mb-1 inline-flex items-center gap-1">
                        <span class="mt-1">Advanced trigger</span>
                        <RfTooltip v-if="triggerType === 'device'" class="w-fit">
                          <template #activator>
                            <RfHelpIcon
                              class="border-hover-goal !h-6 !w-6 rounded-full border border-solid"
                            />
                          </template>
                          <template #tooltip>
                            <div class="w-64">
                              Advanced Triggers are only supported on HTML based devices. Not
                              supported for iOS, Android, or Roku.
                            </div>
                          </template>
                        </RfTooltip>
                      </span>
                    </template>
                  </RfSelect>
                  <RfSwitch
                    v-model="useEventInterval"
                    :disabled="userStore.isReadOnly"
                    title="Event-based"
                  />
                  <RfNumberStepper
                    v-if="!useEventInterval"
                    v-model="editingTrigger.custom_trigger_seconds"
                    title="Advanced interval"
                    unit="s"
                    class="w-48"
                    minMessage="Seconds cannot be less than 0"
                    :disabled="userStore.isReadOnly"
                  />
                </div>
                <div class="mt-2 flex gap-6">
                  <RfButtonNew
                    v-if="!isDelayClickTriggerOpen"
                    type="link"
                    size="link"
                    @click="openDelayClick()"
                  >
                    <RfAddIcon class="h-5 w-5" />
                    Add delay/click trigger
                  </RfButtonNew>
                  <RfButtonNew
                    v-if="!isAdvancedTriggerOpen && !editingTrigger.custom_trigger_id"
                    type="link"
                    size="link"
                    @click="openAdvancedTrigger()"
                  >
                    <RfAddIcon class="h-5 w-5" />
                    Add advanced trigger
                  </RfButtonNew>

                  <RfButtonNew
                    v-if="regexAllowed"
                    type="link"
                    size="link"
                    :disabled="
                      userStore.isReadOnly ||
                      !containsRegexOrWildcards ||
                      !isTriggerFormValid ||
                      isTestButtonDisabled
                    "
                    @click="$refs.regexTester.open()"
                  >
                    {{ testerLabel }}
                  </RfButtonNew>
                </div>
              </div>
            </div>
            <div v-if="filterModeDisabled" class="trigger-setting-clicks mt-5">
              <p class="text-heading-2">Optional Settings</p>
              <div class="mt-1 flex flex-col gap-4">
                <RfSelect
                  v-model="localTriggerOption"
                  variant="outlined"
                  sameWidth
                  itemValue="name"
                  itemKey="value"
                  :disabled="userStore.isReadOnly"
                  placeholder="None"
                  :items="[
                    { name: 'None', value: 'none' },
                    { name: 'Delay', value: 'delay' },
                    { name: 'Click trigger', value: 'click' },
                  ]"
                />
                <RfButtonNew
                  v-if="!isAdvancedTriggerOpen && !editingTrigger.custom_trigger_id"
                  type="link"
                  size="link-sm"
                  class="mr-auto ml-0 inline-flex"
                  @click="openAdvancedTrigger()"
                >
                  <RfAddIcon class="h-5 w-5" />
                  Add advanced trigger
                </RfButtonNew>
                <RfNumberStepper
                  v-if="
                    localTriggerOption === 'delay' ||
                    (localTriggerOption === 'click' &&
                      triggerType === 'device' &&
                      app?.flags?.device_trigger_click_delay)
                  "
                  v-model="editingTrigger.delay_seconds"
                  title="Delay (optional)"
                  unit="s"
                  class="w-48"
                  minMessage="Delay cannot be less than 0"
                  :disabled="userStore.isReadOnly"
                />
                <RfNumberStepper
                  v-if="localTriggerOption === 'click'"
                  v-model="editingTrigger.click_count_threshold"
                  title="Show after (optional)"
                  :unit="clicksUnitLabel"
                  class="mt-2 w-48"
                  minMessage="Clicks cannot be less than 0"
                  :disabled="userStore.isReadOnly"
                />

                <RfInput
                  v-if="localTriggerOption === 'click'"
                  v-model="editingTrigger.click_id"
                  variant="outlined"
                  :title="settings.click_id_label"
                  autoWidth
                  class="w-full"
                  :disabled="userStore.isReadOnly"
                  placeholder="e.g. #cancel"
                />
                <div
                  v-if="isAdvancedTriggerOpen || editingTrigger.custom_trigger_id"
                  class="flex flex-col gap-5"
                >
                  <RfSelect
                    v-if="
                      triggerType !== 'device' ||
                      (triggerType === 'device' && app?.flags?.custom_devices_js_sdk)
                    "
                    v-model="editingTrigger.custom_trigger_id"
                    variant="outlined"
                    itemKey="value"
                    sameWidth
                    itemValue="title"
                    :disabled="userStore.isReadOnly"
                    :items="customTriggers"
                  >
                    <template #title>
                      <span class="mb-1 inline-flex items-center gap-1">
                        <span class="mt-1">Advanced trigger</span>
                        <RfTooltip v-if="triggerType === 'device'" class="w-fit">
                          <template #activator>
                            <RfHelpIcon
                              class="border-hover-goal !h-6 !w-6 rounded-full border border-solid"
                            />
                          </template>
                          <template #tooltip>
                            <div class="w-64">
                              Advanced Triggers are only supported on HTML based devices. Not
                              supported for iOS, Android, or Roku.
                            </div>
                          </template>
                        </RfTooltip>
                      </span>
                    </template>
                  </RfSelect>
                  <RfSwitch
                    v-model="useEventInterval"
                    :disabled="userStore.isReadOnly"
                    title="Event-based"
                  />
                  <RfNumberStepper
                    v-if="!useEventInterval"
                    v-model="editingTrigger.custom_trigger_seconds"
                    title="Advanced interval"
                    unit="s"
                    class="w-48"
                    minMessage="Seconds cannot be less than 0"
                    :disabled="userStore.isReadOnly"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <RfRegexTester
        ref="regexTester"
        :testType="testType"
        :urlPath="editingTrigger.url_path"
        :urlHash="editingTrigger.url_hash"
        :urlHost="editingTrigger.url_host"
        :queryParams="editingTrigger.query_params"
        :useRegex="editingTrigger.use_regex"
      />
    </div>
    <RfDialog
      ref="classicViewDialog"
      title="Are you sure you want to switch to classic view?"
      message="Switching to classic mode will remove all applied filters. This action cannot be undone. Do you want to continue?"
      confirmText="Confirm"
      keepOpenOnConfirm
      @confirm="confirmToggle"
      @close="onClassicViewClose"
    />
  </RfDialog>
</template>

<script setup>
import { computed, nextTick, ref, watch, onMounted } from "vue";
import RfRegexTester from "@/components/RfCommonCards/RfRegexTester.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import RfButtonTabs from "@/components/buttons/RfButtonTabs.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfAdvancedArguments from "@/blocks/RfAdvancedArguments.vue";
import RfChevronIcon from "../icons/RfChevronIcon.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfNumberStepper from "@/components/inputs/RfNumberStepper.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfAddIcon from "@/components/icons/RfAddIcon.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import { useActionsStore } from "@/pinia/actionsStore";
import { useToastsStore } from "@/pinia/toastsStore";
import { useTriggersStore } from "@/pinia/triggersStore";
import { useUserStore } from "@/pinia/userStore";
import { useTriggerFilterRows } from "@/utils/composables/useTriggerFilterRows";
import { useTriggerUrlValidation } from "@/utils/composables/useTriggerUrlValidation";
import {
  rowValueItems as rowValueItemsUtil,
  isTriggerFormValid as isTriggerFormValidUtil,
  confirmDisabledMessage as confirmDisabledMessageUtil,
  resolveFilterModeToggle,
} from "@/utils/triggerFormUtils";

const props = defineProps({
  editTrigger: { type: Object, default: null },
  creating: { type: Boolean, default: false },
  app: { type: Object, default: null },
  parentTriggerType: { type: String, default: null },
  width: { type: Number, default: null },
});
const emit = defineEmits(["closeNewTriggerDialog"]);

const actionsStore = useActionsStore();
const toastsStore = useToastsStore();
const triggersStore = useTriggersStore();
const userStore = useUserStore();

const newTriggerDialog = ref();
const classicViewDialog = ref();
const regexTester = ref();
const advancedArgsBody = ref();

const tabs = [
  { label: "Web", value: "web" },
  { label: "Device", value: "device" },
  { label: "Push", value: "push_event" },
];
const filterTypes = [
  { name: "Domain name", value: "url_host" },
  { name: "Page URL Path", value: "url_path" },
  { name: "Query Parameters", value: "query_params" },
  { name: "URL Hash", value: "url_hash" },
];
const matchTypeItems = [
  { name: "Include", value: "include" },
  { name: "Exclude", value: "exclude" },
];
const descriptions = {
  web: {
    url_path: "page",
    url_path_label: "Page url that triggers the prompt",
    url_path_placeholder: "/settings",
    click_id_label: "Query selector",
    click_id_placeholder: "#cancel-button",
    query_params_label: "Query parameters that trigger the prompt",
    query_params_placeholder: "version=2020",
    url_hash_label: "Url hash that triggers the prompt",
    url_hash_placeholder: "#profile",
    url_host_label: "Page hostname that triggers the prompt",
    url_host_placeholder: "www.example.com",
  },
  device: {
    url_path: "screen",
    url_path_label: "View controller where the prompt appears",
    url_path_placeholder: "ViewController",
    click_id_label: "Button name",
    click_id_placeholder: "cancel",
  },
};
const eventInterval = 7200;

const initialFilterModeDisabled = ref(false);
const hasUnsavedChanges = ref(false);
const classicConfirmed = ref(false);
const submitting = ref(false);
const createdTrigger = ref(null);
const localTriggerOption = ref(null);
const isDelayClickTriggerOpen = ref(false);
const isAdvancedTriggerOpen = ref(false);
const editingTrigger = ref(props.editTrigger);
const clickEnabled = ref(false);
const anyPageSelected = ref(true);
const useEventInterval = ref(false);
const triggerType = ref(props.parentTriggerType || "web");
let initialTriggerState = null;

const {
  filterRows,
  filterOptions,
  filterModeDisabled,
  isFilterOptionsEmpty,
  initializeFilterRows,
  addRow,
  removeRow,
  onFilterTypeSelected,
  isMatchTypeDisabled,
  updateRowValues,
  removeRowValue,
} = useTriggerFilterRows(editingTrigger);

const { regexAllowed, testType, testerLabel, containsRegexOrWildcards, urlFieldErrorMessage } =
  useTriggerUrlValidation(editingTrigger, triggerType);

const rowValueItems = row => rowValueItemsUtil(row, props.app);

const isTestButtonDisabled = computed(
  () =>
    hasUnsavedChanges.value ||
    (!editingTrigger.value.url_path &&
      !editingTrigger.value.query_params &&
      !editingTrigger.value.url_hash &&
      !editingTrigger.value.url_host),
);
const isTabDisabled = computed(
  () => userStore.isReadOnly || !!props.parentTriggerType || !!editingTrigger.value.id,
);
const clientActions = computed(() => actionsStore.clientActions || []);
const pushEventOptions = computed(() => triggersStore.pushEventOptions || []);
const newTrigger = computed(() => {
  const trigger = {
    url_path: "*",
    use_regex: false,
    click_id: null,
    delay_seconds: 0,
    trigger_type: triggerType.value,
  };
  if (triggerType.value === "web") {
    trigger.click_count_threshold = 0;
    trigger.custom_trigger_id = null;
    trigger.custom_trigger_seconds = 2;
    trigger.query_params = null;
    trigger.url_hash = null;
    trigger.url_host = null;
    trigger.filter_options = {
      url_path: [],
      query_params: [],
      url_hash: [],
      url_host: [],
      enabled: !initialFilterModeDisabled.value,
    };
  }
  if (triggerType.value === "push_event") {
    trigger.event_name = "";
    trigger.event_attributes = {};
  }
  return trigger;
});
const settings = computed(() => descriptions[triggerType.value] || descriptions.web);
const activeTabIndex = computed(() =>
  Math.max(
    0,
    tabs.findIndex(tab => tab.value === triggerType.value),
  ),
);
const delayClickTabIndex = computed(() => (localTriggerOption.value === "click" ? 1 : 0));
const regexTabIndex = computed(() => (editingTrigger.value.use_regex === true ? 1 : 0));
const pageScope = computed({
  get: () => (anyPageSelected.value ? "any" : "specific"),
  set: value => {
    anyPageSelected.value = value === "any";
  },
});
const pageScopeOptions = computed(() => [
  { value: "any", label: `Any ${settings.value.url_path}` },
  { value: "specific", label: `Specific ${settings.value.url_path}` },
]);
const submitEnabled = computed(() => {
  if (
    triggerType.value === "push_event" &&
    (!editingTrigger.value?.event_name || advancedArgsBody.value?.hasError)
  )
    return false;

  if (!editingTrigger.value) return false;
  const anyPageSelectedInFilterMode = !isFilterOptionsEmpty() && !filterModeDisabled.value;

  let requirementsFilled =
    !!editingTrigger.value.url_path ||
    !!editingTrigger.value.query_params ||
    !!editingTrigger.value.url_hash ||
    !!editingTrigger.value.url_host ||
    anyPageSelectedInFilterMode;
  if (clickEnabled.value) {
    requirementsFilled = requirementsFilled && !!editingTrigger.value.click_id;
    if (triggerType.value !== "device") {
      requirementsFilled = requirementsFilled && !!editingTrigger.value.click_count_threshold;
    }
  }

  if (editingTrigger.value.custom_trigger_id) {
    requirementsFilled =
      requirementsFilled &&
      (useEventInterval.value ||
        (!!editingTrigger.value.custom_trigger_seconds &&
          parseInt(editingTrigger.value.custom_trigger_seconds) > 0));
  }
  return requirementsFilled;
});
const confirmDisabledMessage = computed(() =>
  confirmDisabledMessageUtil({
    isDisabledWithMessage: userStore.isDisabledWithMessage,
    editingTrigger: editingTrigger.value,
    submitEnabled: submitEnabled.value,
  }),
);
const customTriggers = computed(() => {
  if (props.app) {
    const blank = [{ title: "None", value: "" }];
    const actions = clientActions.value
      .filter(action => action.action_type === "custom_trigger")
      .map(action => ({ title: action.name, value: action.id }));
    return blank.concat(actions);
  }
  return [];
});
const isTriggerFormValid = computed(() =>
  isTriggerFormValidUtil({
    filterModeDisabled: filterModeDisabled.value,
    editingTrigger: editingTrigger.value,
    triggerType: triggerType.value,
  }),
);

watch(anyPageSelected, newValue => {
  if (!newValue) return;
  Object.keys(filterOptions.value).forEach(key => {
    if (Array.isArray(filterOptions.value[key])) {
      filterOptions.value[key] = [];
    }
  });
  filterRows.value = [{ filterType: null, matchType: null, values: [] }];
});
watch(
  localTriggerOption,
  newValue => {
    if (newValue === "delay") {
      if (triggerType.value !== "device" || !props.app?.flags?.device_trigger_click_delay) {
        editingTrigger.value.click_id = null;
        clickEnabled.value = false;
      } else {
        clickEnabled.value = !!editingTrigger.value.click_id;
      }
    } else if (newValue === "click") {
      if (triggerType.value !== "device" || !props.app?.flags?.device_trigger_click_delay) {
        editingTrigger.value.delay_seconds = 0;
      }
      clickEnabled.value = true;
    }
  },
  { immediate: true },
);
watch(
  editingTrigger,
  newVal => {
    hasUnsavedChanges.value =
      JSON.stringify(newVal.filter_options) !== JSON.stringify(initialTriggerState);
  },
  { deep: true },
);
watch(clickEnabled, to => {
  if (to && !editingTrigger.value.click_count_threshold) {
    editingTrigger.value.click_count_threshold = 1;
  }
});
watch(
  triggerType,
  to => {
    if (to === "push_event") triggersStore.getCustomEvents(props.app.id);
    if (props.creating) editingTrigger.value = newTrigger.value;
  },
  { immediate: true },
);

const clicksUnitLabel = value => (value === 1 ? "click" : "clicks");

const saveInitialState = () => {
  initialTriggerState = JSON.parse(JSON.stringify(editingTrigger.value.filter_options));
  hasUnsavedChanges.value = false;
};
const confirmToggle = () => {
  filterOptions.value = {
    url_path: [],
    query_params: [],
    url_hash: [],
    url_host: [],
    enabled: false,
  };
  filterRows.value = [];
  anyPageSelected.value = true;
  isDelayClickTriggerOpen.value = false;
  localTriggerOption.value = "none";
  classicConfirmed.value = true;
  classicViewDialog.value?.close();
};
const onClassicViewClose = () => {
  classicConfirmed.value = false;
};
const handleFilterModeToggle = newValue => {
  const action = resolveFilterModeToggle({
    newValue,
    isFilterOptionsEmpty: isFilterOptionsEmpty(),
  });
  if (action === "enableFilterBuilder") {
    filterModeDisabled.value = false;
    initializeFilterRows();
  } else if (action === "enableClassicView") {
    filterModeDisabled.value = true;
  } else {
    classicViewDialog.value?.open();
  }
};
const changeTab = newTriggerType => {
  if (triggerType.value === "web") {
    initialFilterModeDisabled.value = filterModeDisabled.value;
  }

  triggerType.value = newTriggerType;

  if (newTriggerType === "device" || newTriggerType === "push_event") {
    filterModeDisabled.value = true;
  } else if (newTriggerType === "web") {
    filterModeDisabled.value = initialFilterModeDisabled.value ?? false;
  }
};
const openDelayClick = () => {
  isDelayClickTriggerOpen.value = true;
  localTriggerOption.value = "delay";
};
const openAdvancedTrigger = () => {
  editingTrigger.value.custom_trigger_seconds = 2;
  isDelayClickTriggerOpen.value = false;
  isAdvancedTriggerOpen.value = true;
};
const submitTrigger = async () => {
  editingTrigger.value.trigger_type = triggerType.value;
  if (clickEnabled.value) {
    if (triggerType.value !== "device" || !props.app?.flags?.device_trigger_click_delay) {
      editingTrigger.value.delay_seconds = 0;
    }
  } else {
    if (triggerType.value !== "device" || !props.app?.flags?.device_trigger_click_delay) {
      editingTrigger.value.click_id = null;
    }
    if (triggerType.value !== "device") {
      editingTrigger.value.click_count_threshold = 0;
    }
  }
  if (anyPageSelected.value) {
    editingTrigger.value.url_path = "*";
    editingTrigger.value.use_regex = false;
    editingTrigger.value.query_params = null;
    editingTrigger.value.url_hash = null;
    editingTrigger.value.url_host = null;
  }

  if (!editingTrigger.value.custom_trigger_id) {
    editingTrigger.value.custom_trigger_seconds = null;
  }
  if (useEventInterval.value && editingTrigger.value.custom_trigger_id) {
    editingTrigger.value.custom_trigger_seconds = eventInterval;
  }
  if (triggerType.value === "push_event") {
    editingTrigger.value.event_attributes = advancedArgsBody.value?.get?.();
  }
  if (!isTriggerFormValid.value) {
    return;
  }
  submitting.value = true;
  let newlyCreatedTrigger = null;
  if (props.creating) {
    newlyCreatedTrigger = await triggersStore.createTrigger({
      appId: props.app.id,
      newTrigger: editingTrigger.value,
    });
  } else {
    await triggersStore.updateTrigger({
      appId: props.app.id,
      triggerId: editingTrigger.value.id,
      model: editingTrigger.value,
    });
  }
  submitting.value = false;
  toastsStore.create({
    type: "success",
    body: `Trigger ${props.creating ? "created" : "updated"} successfully`,
  });
  createdTrigger.value = newlyCreatedTrigger;
  newTriggerDialog.value?.close();
};
const handleDialogClose = () => {
  const triggerToAssign = createdTrigger.value;
  createdTrigger.value = null;
  emit("closeNewTriggerDialog", triggerToAssign);
};

onMounted(() => {
  if (!clientActions.value.length) actionsStore.getClientActions(props.app.id);

  if (props.creating) {
    clickEnabled.value = false;
    anyPageSelected.value = true;
    useEventInterval.value = false;
    localTriggerOption.value = "none";
    editingTrigger.value = {
      ...newTrigger.value,
      filter_options: {
        ...(newTrigger.value?.filter_options || {}),
        ...(triggerType.value === "web" && {
          url_path: [],
          query_params: [],
          url_hash: [],
          url_host: [],
          enabled: true,
        }),
      },
    };
  } else {
    triggerType.value = editingTrigger.value.trigger_type;
    useEventInterval.value = editingTrigger.value.custom_trigger_seconds === eventInterval;
    clickEnabled.value = !!editingTrigger.value.click_id;
    localTriggerOption.value =
      editingTrigger.value.trigger_type === "device" &&
      editingTrigger.value.click_id &&
      props.app?.flags?.device_trigger_click_delay
        ? "click"
        : editingTrigger.value.delay_seconds > 0
          ? "delay"
          : editingTrigger.value.click_id
            ? "click"
            : "none";
    isDelayClickTriggerOpen.value = localTriggerOption.value !== "none";
    const anyPageSelectedInFilterMode = isFilterOptionsEmpty() && !filterModeDisabled.value;
    anyPageSelected.value =
      (editingTrigger.value.url_path === "*" &&
        !editingTrigger.value.query_params &&
        !editingTrigger.value.url_hash &&
        !editingTrigger.value.url_host) ||
      anyPageSelectedInFilterMode;
  }

  if (!filterModeDisabled.value) {
    initializeFilterRows();
  }
  if (triggerType.value === "web") {
    saveInitialState();
  }
  nextTick(() => newTriggerDialog.value?.open());
});
</script>

<style lang="scss" scoped>
::v-deep(.trigger-specific-pg-opt) {
  margin: 12px 0 0 0 !important;
}
</style>
